import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Loader2,
    Check,
    ChevronRight,
    SkipForward,
} from "lucide-react";
import {
    getOnboardingState,
    submitOnboarding,
    skipOnboarding,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { track as trackEvent } from "@/lib/analytics";

// Display labels and the inline copy for each option. Order matches the
// product spec — the backend keeps the canonical enum order in models.py.
const USER_TYPE_OPTIONS = [
    { key: "first_time_buyer", label: "First-Time Homebuyer", hint: "Buying my first home" },
    { key: "repeat_buyer", label: "Repeat Homebuyer", hint: "I've bought a home before" },
    { key: "homeowner", label: "Homeowner", hint: "I own my home now" },
    { key: "investor", label: "Real Estate Investor", hint: "Buying rentals or flips" },
    { key: "agent", label: "Real Estate Agent", hint: "Selling homes for clients" },
    { key: "property_manager", label: "Property Manager", hint: "Managing properties" },
    { key: "contractor", label: "Contractor", hint: "I do home repairs" },
    { key: "inspector", label: "Inspector", hint: "I write inspection reports" },
    { key: "other", label: "Other", hint: "None of the above" },
];

const AGE_RANGE_OPTIONS = [
    { key: "under_25", label: "Under 25" },
    { key: "25_34", label: "25–34" },
    { key: "35_44", label: "35–44" },
    { key: "45_54", label: "45–54" },
    { key: "55_64", label: "55–64" },
    { key: "65_plus", label: "65+" },
    { key: "prefer_not_to_say", label: "Prefer not to say" },
];

const ACQUISITION_SOURCE_OPTIONS = [
    { key: "google", label: "Google Search" },
    { key: "pinterest", label: "Pinterest" },
    { key: "facebook", label: "Facebook" },
    { key: "instagram", label: "Instagram" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "referral", label: "Referral (friend/family)" },
    { key: "agent", label: "Real Estate Agent" },
    { key: "inspector", label: "Home Inspector" },
    { key: "contractor", label: "Contractor" },
    { key: "email", label: "Email" },
    { key: "other", label: "Other" },
];

const PRIMARY_GOAL_OPTIONS = [
    { key: "understand_report", label: "Understand an inspection report" },
    { key: "estimate_costs", label: "Estimate repair costs" },
    { key: "prepare_negotiation", label: "Prepare for negotiation" },
    { key: "compare_contractors", label: "Compare contractors" },
    { key: "evaluate_investment", label: "Evaluate an investment property" },
    { key: "research_issues", label: "Research home issues" },
    { key: "other", label: "Other" },
];

export default function Onboarding() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({
        user_type: null,
        age_range: null,
        acquisition_source: null,
        primary_goal: null,
    });
    const [submitting, setSubmitting] = useState(false);
    const [skipping, setSkipping] = useState(false);

    useEffect(() => {
        // If the user lands here already onboarded, bounce them home.
        let alive = true;
        (async () => {
            try {
                const r = await getOnboardingState();
                if (!alive) return;
                if (r.onboarding_completed) {
                    navigate("/dashboard", { replace: true });
                    return;
                }
                if (r.answers) {
                    setAnswers((prev) => ({ ...prev, ...r.answers }));
                }
                trackEvent("onboarding_started", {});
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [navigate]);

    const setAnswer = (field, value) => {
        setAnswers((prev) => ({ ...prev, [field]: value }));
        trackEvent(`${field}_selected`, { value });
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const r = await submitOnboarding(answers);
            trackEvent("onboarding_completed", {
                user_type: answers.user_type,
                age_range: answers.age_range,
                acquisition_source: answers.acquisition_source,
                primary_goal: answers.primary_goal,
            });
            toast.success("Thanks — your preferences are saved.");
            if (r?.user) await refresh?.();
            navigate("/dashboard");
        } catch {
            toast.error("Couldn't save your answers. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSkip = async () => {
        setSkipping(true);
        try {
            await skipOnboarding();
            trackEvent("onboarding_skipped", {});
            await refresh?.();
            navigate("/dashboard");
        } catch {
            toast.error("Couldn't skip. Please try again.");
        } finally {
            setSkipping(false);
        }
    };

    const anyAnswered = useMemo(
        () => Object.values(answers).some((v) => v !== null && v !== undefined),
        [answers],
    );

    if (loading || !user) {
        return (
            <div
                className="min-h-[60vh] flex items-center justify-center"
                data-testid="onboarding-loading"
            >
                <Loader2 className="h-5 w-5 animate-spin text-bl-primary" />
            </div>
        );
    }

    return (
        <main
            className="min-h-screen bg-bl-bg py-8 sm:py-12"
            data-testid="onboarding-page"
        >
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                {/* Header */}
                <header className="mb-6 sm:mb-8">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-bl-muted mb-2">
                        Step 1 of 1 · 15 seconds
                    </p>
                    <h1
                        className="font-display font-semibold text-3xl sm:text-4xl text-bl mb-2"
                        data-testid="onboarding-headline"
                    >
                        Help us personalize your experience
                    </h1>
                    <p className="text-sm text-bl-muted">
                        These questions are optional and help us improve
                        Buyer's Leverage and provide more relevant insights.
                    </p>
                </header>

                {/* Q1 — User type — large selectable cards */}
                <Section title="What best describes you?" required={false}>
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                        data-testid="onboarding-q-user-type"
                    >
                        {USER_TYPE_OPTIONS.map((opt) => (
                            <Card
                                key={opt.key}
                                selected={answers.user_type === opt.key}
                                onClick={() => setAnswer("user_type", opt.key)}
                                testId={`onboarding-user-type-${opt.key}`}
                                label={opt.label}
                                hint={opt.hint}
                            />
                        ))}
                    </div>
                </Section>

                {/* Q2 — Age range — compact selectable chips */}
                <Section title="What age range are you in?">
                    <div
                        className="flex flex-wrap gap-2"
                        data-testid="onboarding-q-age-range"
                    >
                        {AGE_RANGE_OPTIONS.map((opt) => (
                            <Chip
                                key={opt.key}
                                selected={answers.age_range === opt.key}
                                onClick={() => setAnswer("age_range", opt.key)}
                                testId={`onboarding-age-range-${opt.key}`}
                                label={opt.label}
                            />
                        ))}
                    </div>
                </Section>

                {/* Q3 — Acquisition source — compact chips */}
                <Section title="How did you hear about Buyer's Leverage?">
                    <div
                        className="flex flex-wrap gap-2"
                        data-testid="onboarding-q-acquisition-source"
                    >
                        {ACQUISITION_SOURCE_OPTIONS.map((opt) => (
                            <Chip
                                key={opt.key}
                                selected={answers.acquisition_source === opt.key}
                                onClick={() => setAnswer("acquisition_source", opt.key)}
                                testId={`onboarding-acquisition-source-${opt.key}`}
                                label={opt.label}
                            />
                        ))}
                    </div>
                </Section>

                {/* Q4 — Primary goal — compact chips */}
                <Section title="What are you hoping to accomplish today?">
                    <div
                        className="flex flex-wrap gap-2"
                        data-testid="onboarding-q-primary-goal"
                    >
                        {PRIMARY_GOAL_OPTIONS.map((opt) => (
                            <Chip
                                key={opt.key}
                                selected={answers.primary_goal === opt.key}
                                onClick={() => setAnswer("primary_goal", opt.key)}
                                testId={`onboarding-primary-goal-${opt.key}`}
                                label={opt.label}
                            />
                        ))}
                    </div>
                </Section>

                {/* Privacy note */}
                <p className="mt-6 text-xs text-bl-muted">
                    Information is used only to improve Buyer's Leverage and
                    better understand our users. We never share or sell your
                    demographics.
                </p>

                {/* Sticky bottom CTA bar */}
                <div className="mt-6 flex items-center gap-3 flex-wrap">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || skipping || !anyAnswered}
                        className="bl-btn-primary text-sm gap-1.5"
                        data-testid="onboarding-continue-btn"
                        title={
                            !anyAnswered
                                ? "Answer at least one question, or skip for now"
                                : "Save your preferences"
                        }
                    >
                        {submitting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                        Continue
                    </button>
                    <button
                        type="button"
                        onClick={handleSkip}
                        disabled={submitting || skipping}
                        className="bl-btn-ghost text-sm gap-1.5"
                        data-testid="onboarding-skip-btn"
                    >
                        {skipping ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <SkipForward className="h-4 w-4" />
                        )}
                        Skip for now
                    </button>
                    <span className="text-xs text-bl-muted ml-auto">
                        You can change these anytime in Profile Settings.
                    </span>
                </div>
            </div>
        </main>
    );
}

function Section({ title, children }) {
    return (
        <section className="bl-card p-4 sm:p-5 mb-4">
            <h2 className="font-display font-medium text-base sm:text-lg text-bl mb-3">
                {title}
            </h2>
            {children}
        </section>
    );
}

function Card({ selected, onClick, label, hint, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={selected}
            className={
                "flex items-start gap-2.5 text-left p-3 sm:p-4 rounded-lg border transition-colors "
                + (selected
                    ? "border-bl-primary bg-[var(--bl-teal-tint)] text-bl"
                    : "border-bl/40 bg-white hover:border-bl text-bl-text")
            }
        >
            <span
                className={
                    "mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center shrink-0 "
                    + (selected
                        ? "border-bl-primary bg-bl-primary"
                        : "border-bl/40 bg-white")
                }
            >
                {selected && (
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                )}
            </span>
            <span className="flex-1 min-w-0">
                <span className="block font-medium text-sm">{label}</span>
                {hint && (
                    <span className="block text-[11px] text-bl-muted mt-0.5">
                        {hint}
                    </span>
                )}
            </span>
        </button>
    );
}

function Chip({ selected, onClick, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={selected}
            className={
                "px-3 py-1.5 rounded-full border text-xs sm:text-sm transition-colors "
                + (selected
                    ? "border-bl-primary bg-bl-primary text-white"
                    : "border-bl/40 bg-white text-bl-text hover:border-bl")
            }
        >
            {label}
        </button>
    );
}
